import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import type { ReportPeriodRange } from '../reports/reports.type'
import {
  PLATFORM_COST_BUCKET,
  PLATFORM_COST_INCEPTION_START_DATE,
  PLATFORM_COST_METRIC_KEYS,
  PLATFORM_COST_REPORT_TYPE,
} from './platform-cost-metrics.constant'
import type {
  PlatformCostBucket,
  PlatformCostBucketSize,
  PlatformCostChartSeries,
  PlatformCostDateFilterInput,
  PlatformCostExportParams,
  PlatformCostMetricKey,
  PlatformCostMetricsData,
  PlatformCostMetricsQueryArgs,
  PlatformCostTotals,
} from './platform-cost-metrics.type'

/**
 * Narrows unknown API values to plain records before reading envelope fields.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Formats a Date as an inclusive API calendar day in local time.
 */
export const toApiDateString = (date: Date): string =>
  format(date, 'yyyy-MM-dd')

/**
 * Labels a bucket for charts/tables. Day buckets show one date; week/month
 * show the inclusive period so coarse buckets stay unambiguous.
 */
export const formatPlatformCostBucketLabel = ({
  periodStart,
  periodEnd,
  bucket,
}: {
  periodStart: string
  periodEnd: string
  bucket: PlatformCostBucketSize
}): string => {
  const start = parseISO(periodStart)
  const end = parseISO(periodEnd)
  if (!isValid(start) || !isValid(end)) {
    return periodStart === periodEnd
      ? periodStart
      : `${periodStart}–${periodEnd}`
  }

  if (bucket === PLATFORM_COST_BUCKET.DAY || periodStart === periodEnd) {
    return format(start, 'MMM d, yyyy')
  }

  if (bucket === PLATFORM_COST_BUCKET.MONTH) {
    return format(start, 'MMM yyyy')
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'MMM d')}–${format(end, 'MMM d, yyyy')}`
  }

  return `${format(start, 'MMM d, yyyy')}–${format(end, 'MMM d, yyyy')}`
}

/**
 * Resolves DateFilterNav preset/custom state into concrete dates required by
 * the platform-cost metrics GET endpoint.
 */
export const resolvePlatformCostMetricsDateRange = ({
  isCustomDateRange,
  filter,
  startDate,
  endDate,
  referenceDate = new Date(),
}: PlatformCostDateFilterInput): {
  startDate: string
  endDate: string
} | null => {
  if (isCustomDateRange) {
    if (startDate === undefined || startDate === '') return null
    if (endDate === undefined || endDate === '') return null
    const start = parseISO(startDate)
    const end = parseISO(endDate)
    if (!isValid(start) || !isValid(end) || end < start) return null
    return { startDate, endDate }
  }

  if (filter === undefined) return null

  return resolvePresetDateRange({ filter, referenceDate })
}

/**
 * Maps a house report period preset onto inclusive YYYY-MM-DD bounds.
 */
const resolvePresetDateRange = ({
  filter,
  referenceDate,
}: {
  filter: ReportPeriodRange
  referenceDate: Date
}): { startDate: string; endDate: string } | null => {
  const today = toApiDateString(referenceDate)

  switch (filter) {
    case 'today':
      return { startDate: today, endDate: today }
    case 'yesterday': {
      const yesterday = toApiDateString(subDays(referenceDate, 1))
      return { startDate: yesterday, endDate: yesterday }
    }
    case 'this_week':
      return {
        startDate: toApiDateString(
          startOfWeek(referenceDate, { weekStartsOn: 1 }),
        ),
        endDate: today,
      }
    case 'last_week': {
      const previousWeek = subWeeks(referenceDate, 1)
      return {
        startDate: toApiDateString(
          startOfWeek(previousWeek, { weekStartsOn: 1 }),
        ),
        endDate: toApiDateString(endOfWeek(previousWeek, { weekStartsOn: 1 })),
      }
    }
    case 'this_month':
      return {
        startDate: toApiDateString(startOfMonth(referenceDate)),
        endDate: today,
      }
    case 'last_month': {
      const previousMonth = subMonths(referenceDate, 1)
      return {
        startDate: toApiDateString(startOfMonth(previousMonth)),
        endDate: toApiDateString(endOfMonth(previousMonth)),
      }
    }
    case 'this_year':
      return {
        startDate: toApiDateString(startOfYear(referenceDate)),
        endDate: today,
      }
    case 'last_year': {
      const previousYear = subYears(referenceDate, 1)
      return {
        startDate: toApiDateString(startOfYear(previousYear)),
        endDate: toApiDateString(endOfYear(previousYear)),
      }
    }
    case 'inception_to_date':
      return {
        startDate: PLATFORM_COST_INCEPTION_START_DATE,
        endDate: today,
      }
    default:
      return null
  }
}

/**
 * Builds metrics GET args only when the active date filter resolves cleanly.
 */
export const buildPlatformCostMetricsQueryArgs = ({
  bucket,
  ...dateFilter
}: PlatformCostDateFilterInput & {
  bucket: PlatformCostBucketSize
}): PlatformCostMetricsQueryArgs | null => {
  const range = resolvePlatformCostMetricsDateRange(dateFilter)
  if (range === null) return null
  return {
    startDate: range.startDate,
    endDate: range.endDate,
    bucket,
  }
}

/**
 * Builds export/download params for ExportDataModal / shared report mutations.
 * Preset filters keep backend filter semantics; custom ranges send dates.
 */
export const buildPlatformCostExportParams = ({
  isCustomDateRange,
  startDate,
  endDate,
  filter,
  bucket,
}: {
  isCustomDateRange: boolean
  startDate?: string
  endDate?: string
  filter?: ReportPeriodRange
  bucket: PlatformCostBucketSize
}): PlatformCostExportParams => {
  if (isCustomDateRange) {
    return {
      reportType: PLATFORM_COST_REPORT_TYPE,
      isCustomDateRange: true,
      startDate,
      endDate,
      bucket,
    }
  }

  return {
    reportType: PLATFORM_COST_REPORT_TYPE,
    isCustomDateRange: false,
    filter,
    bucket,
  }
}

/**
 * Maps API buckets into a single-series chart for the selected metric.
 */
export const mapBucketsToChartSeries = ({
  buckets,
  metricKey,
  bucket,
}: {
  buckets: PlatformCostBucket[]
  metricKey: PlatformCostMetricKey
  bucket: PlatformCostBucketSize
}): PlatformCostChartSeries => ({
  labels: buckets.map(({ periodStart, periodEnd }) =>
    formatPlatformCostBucketLabel({ periodStart, periodEnd, bucket }),
  ),
  data: buckets.map((costBucket) => costBucket[metricKey]),
})

/**
 * Checks that totals contain every metric key as a finite number.
 */
const isPlatformCostTotals = (value: unknown): value is PlatformCostTotals => {
  if (!isRecord(value)) return false
  return PLATFORM_COST_METRIC_KEYS.every((metricKey) => {
    const metricValue = value[metricKey]
    return typeof metricValue === 'number' && Number.isFinite(metricValue)
  })
}

/**
 * Checks one bucket row before chart/table mapping.
 */
const isPlatformCostBucket = (value: unknown): value is PlatformCostBucket => {
  if (!isRecord(value)) return false
  if (typeof value.periodStart !== 'string' || value.periodStart === '') {
    return false
  }
  if (typeof value.periodEnd !== 'string' || value.periodEnd === '') {
    return false
  }
  return isPlatformCostTotals(value)
}

/**
 * Guards the metrics success envelope so malformed payloads never reach the UI.
 */
export const isPlatformCostMetricsEnvelope = (
  value: unknown,
): value is {
  success: boolean
  message: string
  data: PlatformCostMetricsData
} => {
  if (!isRecord(value)) return false
  if (value.success !== true) return false
  if (!isRecord(value.data)) return false
  const { totals, buckets } = value.data
  if (!isPlatformCostTotals(totals)) return false
  if (!Array.isArray(buckets)) return false
  return buckets.every(isPlatformCostBucket)
}
